import { HttpService } from '@/data/http/http-service';
import { PdfLocaFilelUrl } from '@/domain/pdf-reports/pdf-reports.type';
import { ExportReportProjectActionParams } from '@/domain/project/actions/export-report-project.action';
import { ProjectFestival } from '@/domain/project-festival/entities/project-festival.entity';
import { ProjectFestivalGateway } from '@/domain/project-festival/gateways/project-festival.gateway';

import {
  getProjectDtoSchema,
  getProjectsDtoSchema,
} from './project-festival.dto.schema';
import { ProjectMapper } from './project-festival.mapper';

export class ApiProjectHttpGateway implements ProjectFestivalGateway {
  private httpProject = new HttpService({
    baseURL: import.meta.env.VITE_API_PROJECT,
  });
  private httpImpact = new HttpService({
    baseURL: import.meta.env.VITE_API_IMPACT,
  });

  async getAll(): Promise<ProjectFestival[]> {
    const url = '/festivals';
    const options = { params: { $limit: 1000 } };
    const response = await this.httpProject.get(url, options);
    const result = getProjectsDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    const projects: ProjectFestival[] = result.data.data.map((x) =>
      ProjectMapper.dtoToEntity(x),
    );
    return projects;
  }

  async get(id: number): Promise<ProjectFestival> {
    const url = `/festivals/${id}`;
    const response = await this.httpProject.get(url);
    const result = getProjectDtoSchema.safeParse(response.data);
    if (result.success === false) {
      console.error(result.error);
      throw result.error;
    }
    const project = ProjectMapper.dtoToEntity(result.data);
    return project;
  }

  async create(project: ProjectFestival): Promise<void> {
    const url = '/festivals';
    const data = ProjectMapper.entityToDto(project);
    const response = await this.httpProject.post(url, data);
    if (response.status === 201) {
      return;
    } else {
      throw 'error on creation';
    }
  }

  async delete(id: number): Promise<void> {
    const url = `/festivals/${id}`;
    const response = await this.httpProject.delete(url);
    if (response.status === 200) {
      return;
    } else {
      throw 'error on delete';
    }
  }

  async edit(project: ProjectFestival): Promise<void> {
    const url = `/festivals/${project.id}`;
    const data = ProjectMapper.entityToDto(project);
    const response = await this.httpProject.patch(url, data);
    if (response.status !== 200) {
      throw 'error on edition';
    }
    return;
  }

  async exportPdfReport(
    params: ExportReportProjectActionParams,
  ): Promise<PdfLocaFilelUrl> {
    // Call the pdf service
    const query_params = `?export_type=${params.exportType}&project_type=${params.projectType}`;
    const path = `/project-pdf-reports/${params.projectId}/${params.filename}`;
    const url = `${path}${query_params}`;
    const response = await this.httpProject.get(url, {
      responseType: 'blob',
    });

    if (response.status !== 200) {
      throw new Error(`PDF export failed with status: ${response.status}`);
    }

    if (response.headers['content-type'] !== 'application/pdf') {
      throw new Error('Invalid content type received from PDF service');
    }

    // Create a temporary URL for the Blob, and emulate a download
    const blobObj = await response.data;

    const temporaryFileUrl = URL.createObjectURL(blobObj);
    const link = document.createElement('a');
    link.href = temporaryFileUrl;
    link.setAttribute('download', params.filename);
    link.click();

    // Clean up the temporary URL long after the user receives the notification
    setTimeout(() => URL.revokeObjectURL(temporaryFileUrl), 2 * 60_000); // 2 minutes

    return temporaryFileUrl;
  }

  async duplicate({
    originalProjectId,
    newProjectName,
    copyImpactCo2,
    copyImpactEss,
    copyImpactEnv,
  }: {
    originalProjectId: number;
    newProjectName: string;
    copyImpactCo2: boolean;
    copyImpactEss: boolean;
    copyImpactEnv: boolean;
  }) {
    const callDuplicateProject = async (
      originalProjectId: number,
      newProjectName: string,
    ): Promise<number> => {
      const url = '/duplicate-project';
      const data = { originalProjectId, newProjectName };
      const response = await this.httpProject.post(url, data);
      if (response.status !== 201) {
        throw 'error on duplicate (project)';
      }
      return response.data.id;
    };
    const callDuplicateImpactResponses = async (
      originalProjectId: number,
      targetProjectId: number,
    ) => {
      const url = '/duplicate-responses';
      const data = {
        originalProjectId,
        targetProjectId,
        copyImpactCo2,
        copyImpactEss,
        copyImpactEnv,
      };
      const response = await this.httpImpact.post(url, data);
      if (response.status !== 201) {
        throw 'error on duplicate (impact-responses)';
      }
    };
    const newProjectId = await callDuplicateProject(
      originalProjectId,
      newProjectName,
    );
    await callDuplicateImpactResponses(originalProjectId, newProjectId);
  }
}
